import { useEffect, useRef } from 'react';

import { useGeolocationCallback } from '@/geolocation/use-geolocation-callback';
import type { GeolocationResponse } from '@/geolocation/types';
import { isEmbeddedInLobby } from '@/logic/embedded-mode';
import { resolveWebWrapper } from '@/logic/web-wrapper/web-wrapper-bridge';
import {
  setGameContext,
  setGeolocationContext,
  setGeolocationStatus,
  setPreChecksComplete,
} from '@/store/actions';
import { useStore } from '@/store/useStore';
import extractGeolocationToken from '@/utilities/helpers/extractGeolocationToken';
import { resolveLaunchGeoProduct } from './resolve-launch-geo-product';
import { hashValue } from '@/utilities/helpers/hashValue';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

const GEOLOCATION_RESULT_MESSAGE = 'geolocationResult';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isValidGeolocationPayload = (
  type: string,
  payload: Record<string, unknown>,
): boolean => {
  switch (type) {
    case 'aw-geolocation/location-in-progress':
    case 'geolocation-sdk/location-in-progress':
      return true;
    case 'aw-geolocation/geolocation-passed':
    case 'geolocation-sdk/geolocation-passed':
      return typeof payload.jwt === 'string' && payload.jwt.length > 0;
    case 'aw-geolocation/location-failure':
    case 'geolocation-sdk/location-failure':
      return isRecord(payload.error);
    case 'aw-geolocation/geolocation-rejected':
    case 'geolocation-sdk/geolocation-rejected':
      return isRecord(payload.rejection);
    case 'aw-geolocation/geolocation-invalidated':
    case 'geolocation-sdk/geolocation-invalidated':
      return (
        typeof payload.invalidation === 'string' || isRecord(payload.rejection)
      );
    default:
      return false;
  }
};

export const isEmbeddedGeolocationResult = (
  value: unknown,
): value is GeolocationResponse => {
  if (!isRecord(value) || typeof value.type !== 'string') return false;
  if (!isRecord(value.payload)) return false;

  return isValidGeolocationPayload(value.type, value.payload);
};

export const useEmbeddedLobbyGeolocation = () => {
  const hasActiveSession = useStore(
    (state) => state.userSessionContext.hasActiveSession,
  );
  const sessionId = useStore((state) => state.userSessionContext.sessionId);
  const geolocationResetRevision = useStore(
    (state) => state.geolocationResetRevision,
  );
  const product = useStore((state) => state.runtime.product);
  const launchGeoProduct = useStore(
    (state) => state.gameContext.launchGeoProduct,
  );
  const { geolocationCallback } = useGeolocationCallback();
  const embedded = isEmbeddedInLobby();
  const lastLocateRequestKey = useRef<string | null>(null);
  const requiredProduct =
    launchGeoProduct && launchGeoProduct !== 'UNKNOWN'
      ? launchGeoProduct
      : resolveLaunchGeoProduct({
          runtimeProduct: product,
          isAndroid: false,
        });

  useEffect(() => {
    if (!embedded) return;

    setPreChecksComplete(true);

    const handleMessage = (event: MessageEvent) => {
      if (
        event.source !== window.parent ||
        event.origin !== window.location.origin ||
        !isRecord(event.data) ||
        event.data.type !== GEOLOCATION_RESULT_MESSAGE ||
        !isEmbeddedGeolocationResult(event.data.detail)
      ) {
        return;
      }

      geolocationCallback(event.data.detail);
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [embedded, geolocationCallback]);

  useEffect(() => {
    if (!embedded || !hasActiveSession) {
      lastLocateRequestKey.current = null;
      return;
    }

    const locateRequestKey = `${sessionId ?? ''}|${geolocationResetRevision}|${requiredProduct}`;
    if (lastLocateRequestKey.current === locateRequestKey) return;
    lastLocateRequestKey.current = locateRequestKey;

    if (launchGeoProduct !== requiredProduct) {
      setGameContext({ launchGeoProduct: requiredProduct });
    }

    const cachedGeoToken = extractGeolocationToken(
      requiredProduct === 'UNKNOWN' ? undefined : requiredProduct,
    );

    if (cachedGeoToken) {
      setGeolocationContext({ geolocationToken: cachedGeoToken });
      setGeolocationStatus({ previouslyVerified: true });

      hashValue(cachedGeoToken).then((geolocationTokenHash) => {
        Logger.logRumAction({
          name: '[GEOLOCATION] Preliminary geolocation success',
          useCase: DdEventCategory.Geolocation,
          eventType: DdEventType.Success,
          context: {
            geolocationTokenHash,
            embedded: true,
          },
        });
      });
    }

    void Promise.resolve(resolveWebWrapper())
      .then((wrapper) =>
        requiredProduct !== 'UNKNOWN'
          ? wrapper?.triggerLocateUserWithDomain(requiredProduct.toLowerCase())
          : wrapper?.triggerLocateUser(),
      )
      .catch(() => undefined);
  }, [
    embedded,
    geolocationResetRevision,
    hasActiveSession,
    launchGeoProduct,
    requiredProduct,
    sessionId,
  ]);
};
